import { SearchItem } from '../../../../types/types';
import KnowledgeBaseCard from '@/components/KnowledgeBaseCard';
import LessonCard from '@/components/Learn/Lesson';
import PodcastCard from '@/pages/podcast/shared/components/podcast-card';
import { ResourceCard } from '@/components/ResourceCard';
import type { ResourceHubItem } from '@/pages/resource-hub/shared/types';
import type { KnowledgeBase, Course, Podcast, Resource } from '@edwin-group/people-graphql';

type KnowledgeBaseWithAlternatives = KnowledgeBase & {
  kbSlug?: string;
};

type PodcastWithAlternatives = Podcast & {
  podcastSlug?: string;
  podcastBriefDescription?: string;
  podcastCover?: Podcast['cover'];
};

type ResourceWithAlternatives = Resource & {
  resourceSlug?: string;
  resourceBriefDescription?: string;
  resourceCover?: Resource['cover'];
};

export type CardItemProps = {
  item: SearchItem;
};

function CardItem({ item }: CardItemProps) {
  switch (item?.__typename) {
    case 'Course': {
      const course = (item.attributes ?? item) as Course;

      return (
        <LessonCard
          title={course.title}
          description={course.description}
          thumbnail={course.thumbnail?.data?.attributes ?? course.thumbnail?.data ?? course.thumbnail}
          url={`/learn/course/${course.slug}`}
          duration={course.duration || ''}
          tag="Course"
        />
      );
    }
    case 'KnowledgeBase': {
      const kb = (item.attributes ?? item) as KnowledgeBaseWithAlternatives;
      const slug = kb.slug ?? kb.kbSlug ?? '';

      return (
        <KnowledgeBaseCard
          title={kb.title}
          description={kb.briefDescription}
          thumbnail={kb.cover?.data?.attributes ?? kb.cover?.data ?? kb.cover}
          url={`/knowledge-base/${slug}`}
          duration={kb.duration || ''}
          tag={
            kb.knowledgeBaseType?.data?.attributes?.type ??
            kb.knowledgeBaseType?.data?.type ??
            kb.knowledgeBaseType?.type
          }
        />
      );
    }
    case 'Podcast': {
      const podcast = (item.attributes ?? item) as PodcastWithAlternatives;
      const normalizedPodcast: Podcast = {
        ...podcast,
        slug: podcast.slug ?? podcast.podcastSlug ?? '',
        briefDescription: podcast.briefDescription ?? podcast.podcastBriefDescription ?? '',
        cover: podcast.cover ?? podcast.podcastCover ?? podcast.cover,
      };

      return <PodcastCard podcast={normalizedPodcast} />;
    }
    case 'Resource': {
      const resource = (item.attributes ?? item) as ResourceWithAlternatives;
      const normalizedResource: ResourceHubItem = {
        ...(resource as ResourceHubItem),
        slug: resource.slug ?? resource.resourceSlug ?? '',
        briefDescription: resource.briefDescription ?? resource.resourceBriefDescription ?? null,
        cover: resource.cover ?? resource.resourceCover ?? null,
      };

      return <ResourceCard resource={normalizedResource} isResourceHub />;
    }
    default:
      return null;
  }
}

export { CardItem };
